import React, { useState, useEffect } from 'react';
import { fetchApi } from '../../utils/api';
import Seo from '../common/seo';

// Global cache for investments data
let investmentsCache = {
  data: null,
  mostRecent: null,
  timestamp: null,
  promise: null
};

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

// Function to check if cache is valid
const isCacheValid = () => {
  return (
    investmentsCache.data !== null &&
    investmentsCache.timestamp !== null &&
    Date.now() - investmentsCache.timestamp < CACHE_DURATION
  );
};

// Helper to sort investments within a group
const sortInvestments = (investments) => {
  return investments.sort((a, b) => {
    // First prioritize site_rank if available
    if (a.site_rank && b.site_rank) {
      return a.site_rank - b.site_rank;
    }
    if (a.site_rank && !b.site_rank) return -1;
    if (!a.site_rank && b.site_rank) return 1;
    
    // Push "Stealth" companies to the bottom
    if (a.company_name === "Stealth" && b.company_name !== "Stealth") return 1;
    if (b.company_name === "Stealth" && a.company_name !== "Stealth") return -1;
    
    // Sort by tagline presence
    if ((!a.company_tagline && !b.company_tagline) || (a.company_tagline && b.company_tagline)) return 0;
    if (a.company_tagline && !b.company_tagline) return -1;
    return 1;
  });
};

// Map batch abbreviations to month numbers for sorting
const batchToMonth = {
  'Jan': 1, 'Feb': 2, 'Mar': 3, 'Apr': 4, 'May': 5, 'Jun': 6,
  'Jul': 7, 'Aug': 8, 'Sep': 9, 'Oct': 10, 'Nov': 11, 'Dec': 12
};

// Function to fetch investments data
const fetchInvestments = async (force = false) => {
  // If we have a valid cache and don't force refresh, return cached data
  if (!force && isCacheValid()) {
    return { grouped: investmentsCache.data, mostRecent: investmentsCache.mostRecent };
  }

  // If there's already a request in flight, return its promise
  if (investmentsCache.promise) {
    return investmentsCache.promise;
  }

  // Create new request promise
  investmentsCache.promise = fetchApi('api/public/website-inputs', {
    method: 'GET',
    params: { investments: 'true' }
  })
    .then(investmentsData => {
      const allInvestments = investmentsData.data || [];
      
      // Find the most recent batch by year and batch month
      let latestYear = 0;
      let latestMonth = 0;
      let latestBatch = '';
      
      allInvestments.forEach(investment => {
        const year = parseInt(investment.year) || 0;
        const batch = investment.batch || '';
        const monthNum = batchToMonth[batch] || 0;
        
        if (year > latestYear || (year === latestYear && monthNum > latestMonth)) {
          latestYear = year;
          latestMonth = monthNum;
          latestBatch = batch;
        }
      });
      
      // Separate most recent batch from the rest
      const mostRecentInvestments = [];
      const otherInvestments = [];
      
      allInvestments.forEach(investment => {
        const year = parseInt(investment.year) || 0;
        const batch = investment.batch || '';
        
        if (year === latestYear && batch === latestBatch) {
          mostRecentInvestments.push(investment);
        } else {
          otherInvestments.push(investment);
        }
      });
      
      // Group remaining investments by year
      const groupedInvestments = otherInvestments.reduce((acc, investment) => {
        const year = investment.year || 'Unknown';
        if (!acc[year]) {
          acc[year] = [];
        }
        acc[year].push(investment);
        return acc;
      }, {});

      // Sort years
      const sortedYears = Object.keys(groupedInvestments).sort((a, b) => {
        const yearA = parseInt(a) || 0;
        const yearB = parseInt(b) || 0;
        return yearA - yearB;
      });

      // Sort investments within each year
      sortedYears.forEach(year => {
        groupedInvestments[year] = sortInvestments(groupedInvestments[year]);
      });

      // Sort most recent investments
      const sortedMostRecent = sortInvestments([...mostRecentInvestments]);

      // Update cache
      investmentsCache.data = groupedInvestments;
      investmentsCache.mostRecent = {
        year: latestYear,
        batch: latestBatch,
        investments: sortedMostRecent
      };
      investmentsCache.timestamp = Date.now();
      
      return { grouped: groupedInvestments, mostRecent: investmentsCache.mostRecent };
    })
    .finally(() => {
      investmentsCache.promise = null;
    });

  return investmentsCache.promise;
};

const INITIAL_DISPLAY_COUNT = 6;

function Investments({ showTitle = true }) {
  const [investments, setInvestments] = useState(investmentsCache.data);
  const [mostRecent, setMostRecent] = useState(investmentsCache.mostRecent);
  const [loading, setLoading] = useState(!isCacheValid());
  const [error, setError] = useState(null);
  const [expandedYears, setExpandedYears] = useState({});

  const toggleYear = (year) => {
    setExpandedYears(prev => ({
      ...prev,
      [year]: !prev[year]
    }));
  };

  useEffect(() => {
    let mounted = true;

    const loadData = async () => {
      if (!isCacheValid()) {
        setLoading(true);
        try {
          const { grouped, mostRecent: recentData } = await fetchInvestments();
          if (mounted) {
            setInvestments(grouped);
            setMostRecent(recentData);
          }
        } catch (err) {
          console.error('Error fetching data:', err);
          if (mounted) {
            setError('Failed to fetch data');
          }
        } finally {
          if (mounted) {
            setLoading(false);
          }
        }
      }
    };

    loadData();

    return () => {
      mounted = false;
    };
  }, []);

  if (loading) return <div className="empty-loading-container"></div>;
  if (error) return <div>Error: {error}</div>;

  return (
    <main className="container">
      {showTitle && (
        <Seo path="/investments" />
      )}
      {showTitle && (
        <>
          <h1 className="page-title">INVESTMENTS</h1>
          <p>We fund 25-30 teams/year.</p>
          <p>A mix of students, dropouts, & grads.</p>
          <p>We back builders with a unique perspective - regardless of industry.</p>
          <p>We've funded software, hardware, robotics, bio, medtech + CPG.</p>
        </>
      )}
      
      {/* Last Cycle Investments */}
      {mostRecent && mostRecent.investments && mostRecent.investments.length > 0 && (
        <div className="year-section">
          <h2 className="year-title">Last Cycle - {mostRecent.batch} {mostRecent.year}</h2>
          <div className="investments-grid">
            {mostRecent.investments.map((item, index) => (
              <div key={index} className="investments-grid-item">
                <div className="text-container">
                  <h4 className="investments-school">{item.college}</h4>
                  <h3 className="investments-name">{item.company_name}</h3>
                  <p className="investments-title">{item.company_tagline}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Previous Years */}
      {Object.entries(investments || {})
      .reverse()
      .map(([year, yearInvestments]) => {
        const isExpanded = expandedYears[year];
        const displayedInvestments = isExpanded 
          ? yearInvestments 
          : yearInvestments.slice(0, INITIAL_DISPLAY_COUNT);
        const hasMore = yearInvestments.length > INITIAL_DISPLAY_COUNT;
        const remainingCount = yearInvestments.length - INITIAL_DISPLAY_COUNT;

        return (
          <div key={year} className="year-section">
            <h2 className="year-title">{year}</h2>
            <div className="investments-grid">
              {displayedInvestments.map((item, index) => (
                <div key={index} className="investments-grid-item">
                  <div className="text-container">
                    <h4 className="investments-school">{item.college}</h4>
                    <h3 className="investments-name">{item.company_name}</h3>
                    <p className="investments-title">{item.company_tagline}</p>
                  </div>
                </div>
              ))}
            </div>
            {hasMore && (
              <span 
                className="see-all-link"
                onClick={() => toggleYear(year)}
              >
                {isExpanded ? 'Show Less' : `See All Investments (+${remainingCount} more)`}
              </span>
            )}
          </div>
        );
      })}
    </main>
  );
}

export default Investments;